import { useEffect, useState } from 'react'
import {
  fetchPortalSettings, savePortalSettings,
  type PortalSettings,
} from '../lib/api.js'

/**
 * Admin → Organisation (decision 66) — Wave9's trading identity, edited in-app
 * so a finance/legal correction never needs a redeploy. The T&Cs library moved
 * to its own Admin page (decision 135); snippets + tokens moved earlier still
 * (decision 111).
 *
 * Decision 172 (Mark, 2026-08-24): the page had grown to 27 fields — it is now
 * SECTIONED, each section collapsible. Sections containing a blank required
 * field open themselves; fully-configured sections start collapsed, so a
 * healthy page opens compact. The blank-count pill sits ON the header, so
 * nothing hides while collapsed. One Save still covers the whole form.
 *
 * ⚠️ Phone and VAT number are Mark's to fill — they ship empty, clearly
 * flagged, and nothing invents them (decision 64).
 */

interface Field { key: string; label: string; hint?: string; multiline?: boolean; optional?: boolean }

const FIELDS: Field[] = [
  { key: 'legal_name', label: 'Legal name' },
  { key: 'company_no', label: 'Company number' },
  { key: 'registered_office', label: 'Registered office' },
  { key: 'trading_address', label: 'Trading address' },
  { key: 'website', label: 'Website' },
  { key: 'email', label: 'Quotes email' },
  { key: 'phone', label: 'Phone', hint: 'Appears in the quote header — fill before the pilot.' },
  { key: 'vat_number', label: 'VAT number', hint: 'Appears in the quote footer (UK correspondence requirements) — fill before the pilot.' },
  { key: 'expected_booking_days', label: 'Expected booking (days from send)', hint: 'the HubSpot Expected Booking Date defaults to the send date plus this many days, refreshed on each resend. A manually-picked date on a quote always wins. Default 60.' },
  { key: 'hs_bcc_address', label: 'HubSpot BCC logging address', hint: 'your account\'s bcc.hubspot.com address (HubSpot → Settings → Objects → Activities). When set, every quote email is BCC\'d here so the send lands on the contact and deal timeline. Blank = no logging.' },
  { key: 'quote_validity_days', label: 'Quote validity (days)', hint: 'the Valid Until default on a new quote — how long the price is promised. Editable per quote; Extend validity pushes it out later. Default 60.' },
  { key: 'quote_open_horizon_days', label: 'Open horizon (days from sent)', hint: 'an undecided quote whose price has lapsed still counts as OPEN in the Quotes list, cockpit pipeline, board pack and Needs attention until this many days after it was sent (shown as "price lapsed"); after that it sits in the Closed archive. Nothing changes on the quote itself. Default 180 — 94% of orders arrive inside it.' },
  { key: 'nudge_unviewed_days', label: 'Nudge: sent but not viewed (days)', hint: 'a sent quote nobody has opened after this many days appears in the cockpit\'s "Needs attention" list. Default 5.' },
  { key: 'nudge_expiring_days', label: 'Nudge: expiring soon (days)', hint: 'an open quote within this many days of its Valid Until date appears in "Needs attention". Default 7.' },
  { key: 'email_intro', label: 'Outbound email — greeting / intro', optional: true, multiline: true, hint: 'the opening of the quote email, above the cover note. Snippet tokens work (e.g. "Dear #FirstName,") and expand per recipient at send. Blank = no greeting (the cover note can carry its own). Wave9-issued quotes only — resellers use their own branding.' },
  { key: 'email_outro', label: 'Outbound email — closing', optional: true, multiline: true, hint: 'an optional closing paragraph below the "view your quotation" button. Snippet tokens work. Blank = nothing added.' },
  { key: 'acceptance_notify_email', label: 'Acceptance notifications also go to', optional: true, hint: 'every "quote ACCEPTED" notification is also sent to this address, alongside the quote owner — a backup ops mailbox. Blank = the owner alone.' },
  { key: 'import_stage_ids', label: 'Import: deal stage ids', optional: true, hint: 'the sales-pipeline stage ids the "HubSpot deal import" job (Scheduled jobs) reads, separated by "|". Stage ids are visible in the kanban board pipeline config or HubSpot’s pipeline settings URL. Blank = the import refuses to run.' },
  { key: 'quotient_archive_cap', label: 'Quotient archive: first-batch cap', optional: true, hint: 'how many deals the "Quotient PDF archive" job (Scheduled jobs) may look at in total before it stops and asks to be raised — a first batch to check Quotient tolerates the fetching. Seeded 100. Blank or 0 = no limit.' },
  { key: 'enquiry_stage_id', label: 'Enquiry: deal stage id', optional: true, hint: 'the sales-pipeline stage new customer-portal quote requests land on (the "New Enquiry" stage id). Blank = quote requests are refused with a “not configured” message.' },
  { key: 'enquiry_notify_email', label: 'Enquiry: notify email', optional: true, hint: 'who hears about each new quote request from the customer portal. Blank = admin@wave9.co.uk.' },
  { key: 'split_stage_id', label: 'Split: shell deal stage id', optional: true, hint: 'the Sales Pipeline stage a split quote’s parent deal (the zeroed shell) moves to — create a dedicated stage in HubSpot for it. Blank = splitting into site deals is refused until set.' },
  { key: 'asset_stock_order_status', label: 'Provisioning: stock order status', optional: true, hint: 'the asset Order Status option value that means "in stock" — the pack’s stock-match counts look for it. Blank = the seeded default ("Stock").' },
  { key: 'asset_stock_stage_id', label: 'Provisioning: stock stage id', optional: true, hint: 'the asset pipeline stage that means "in stock". Blank = the seeded default (the Firewalls/Routers/Software pipeline’s Stock stage).' },
  { key: 'billing_group_months', label: 'Billing: group later-year invoices within (months)', optional: true, hint: 'lines whose anniversaries fall within this many months of each other share one invoice, dated for the earliest — each line keeps its own term. 0 = every anniversary its own invoice. A deal can override it on its Lines card. Applies when a schedule is generated; invoices already in Xero are never regrouped.' },
  { key: 'xero_probe_minutes', label: 'Xero: budget reading, refresh after (minutes)', optional: true, hint: 'how stale the Xero call-budget reading may get before the 10-minute sweep spends ONE call to refresh it. 15 (about 96 calls a day) gives the hour-by-hour picture on Admin → Xero its resolution. 0 = never probe.' },
  { key: 'xero_budget_alert_at', label: 'Xero: email when calls left drop to', optional: true, hint: 'the Xero daily allowance belongs to the WHOLE organisation — every connected app draws on it — so it can run low without the portal doing anything. At or under this number the portal emails the address below, once per episode. Seeded 200. 0 = no email.' },
  { key: 'xero_budget_alert_email', label: 'Xero: budget alert address', optional: true, hint: 'who hears when the organisation\'s Xero call budget is running low. Blank = admin@wave9.co.uk.' },
  { key: 'renewal_window_days', label: 'Renewal window (days)', hint: 'contracts whose end date falls within this many days appear on the Renewals page. Default 120.' },
  { key: 'renewal_notify_email', label: 'Renewal rollover notify address', hint: 'recording a rollover emails this mailbox to extend the term, reset the end date and invoice.' },
  { key: 'renewal_pipeline_id', label: 'Renewals pipeline id', hint: 'the HubSpot "Active Contracts / Renewals" pipeline the Renewals page mirrors.' },
  { key: 'renewal_stage_rollover', label: 'Renewals: Process Rollover stage id', hint: 'where the old deal moves when a rollover is recorded.' },
  { key: 'renewal_stage_renewed', label: 'Renewals: New Contract - In PRJ stage id', hint: 'where the old deal moves when its renewal quote is accepted (or marked renewed).' },
  { key: 'renewal_stage_lost', label: 'Renewals: Renewal Lost stage id', hint: 'where the old deal moves when the customer is not renewing.' },
  { key: 'renewal_stage_at_risk', label: 'Renewals: At Risk stage id', hint: 'where the old deal moves when a renewal is marked at risk from the Renewals page.' },
  { key: 'renewal_due_stages', label: 'Renewals: due-stage ids', hint: 'comma-separated stage ids that count as "up for renewal" regardless of the end date (Final Year, the Renewal Due bands, At Risk).' },
  { key: 'renewal_excluded_stages', label: 'Renewals: excluded stage ids', hint: 'comma-separated end-of-lifecycle stage ids (New Contract - Completed, both Renewal Lost stages) that never create a renewal row, even inside the window.' },
  { key: 'renewal_stage_sent', label: 'Renewals: sent-quote stage id (Sales Pipeline)', hint: 'where a renewal quote’s SALES deal moves when the quote is sent — "Renewal to Chase". Acceptance still uses the normal accepted stage.' },
  { key: 'renewal_link_excluded_stages', label: 'Renewals: link-search excluded stage ids', hint: 'comma-separated Sales Pipeline stage ids the link-deal picker skips (enquiry intake, validation/provisioning/invoicing admin, closed stages).' },
]

/** Decision 172: the five sections. Every FIELDS key must appear exactly once. */
const SECTIONS: Array<{ id: string; title: string; blurb: string; keys: string[] }> = [
  {
    id: 'identity', title: 'Trading identity',
    blurb: 'Rendered on every quote document — header, footer and the issuer block.',
    keys: ['legal_name', 'company_no', 'registered_office', 'trading_address', 'website', 'email', 'phone', 'vat_number'],
  },
  {
    id: 'quoteEmail', title: 'Quote email',
    blurb: 'The wrapper around every outbound quote email, and where sends get logged.',
    keys: ['email_intro', 'email_outro', 'hs_bcc_address'],
  },
  {
    id: 'quoting', title: 'Quoting & notifications',
    blurb: 'Quote validity and the open horizon, the booking-date default, the cockpit nudge thresholds, and the acceptance ops copy.',
    keys: ['quote_validity_days', 'quote_open_horizon_days', 'expected_booking_days', 'nudge_unviewed_days', 'nudge_expiring_days', 'acceptance_notify_email'],
  },
  {
    id: 'stages', title: 'Imports, enquiries, deal split & provisioning',
    blurb: 'The Sales Pipeline stage ids behind the deal import, customer quote requests and the per-site split.',
    keys: ['import_stage_ids', 'quotient_archive_cap', 'enquiry_stage_id', 'enquiry_notify_email', 'split_stage_id', 'asset_stock_order_status', 'asset_stock_stage_id'],
  },
  {
    id: 'billing', title: 'Billing',
    blurb: 'How the invoice schedule is drawn for a deal that has just been handed to billing.',
    keys: ['billing_group_months'],
  },
  {
    id: 'xero-budget', title: 'Xero call budget',
    blurb: 'Xero grants the portal its own daily call limit (set by the app\'s Xero plan — Core: 5,000) — how often the portal reads what is left, and when it says so.',
    keys: ['xero_probe_minutes', 'xero_budget_alert_at', 'xero_budget_alert_email'],
  },
  {
    id: 'renewals', title: 'Renewals',
    blurb: 'The Active Contracts mirror — window, notify address, and every outcome stage id.',
    keys: [
      'renewal_window_days', 'renewal_notify_email', 'renewal_pipeline_id', 'renewal_stage_rollover',
      'renewal_stage_renewed', 'renewal_stage_lost', 'renewal_stage_at_risk', 'renewal_due_stages',
      'renewal_excluded_stages', 'renewal_stage_sent', 'renewal_link_excluded_stages',
    ],
  },
]

const fieldByKey = new Map(FIELDS.map((f) => [f.key, f]))

export function Organisation() {
  const [data, setData] = useState<PortalSettings | null>(null)
  const [form, setForm] = useState<Record<string, string>>({})
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [open, setOpen] = useState<Record<string, boolean>>({})

  const load = () => {
    fetchPortalSettings().then((d) => {
      setData(d)
      setForm(d.settings)
      setDirty(false)
      // Decision 172: sections holding a blank REQUIRED field open themselves;
      // fully-configured sections start collapsed — a healthy page opens compact.
      setOpen(Object.fromEntries(SECTIONS.map((s) => [
        s.id,
        s.keys.some((k) => !fieldByKey.get(k)?.optional && !d.settings[k]?.trim()),
      ])))
    }).catch((e: Error) => setError(e.message))
  }
  useEffect(load, [])

  if (error && !data) return <p className="pill pill--stop">{error}</p>
  if (!data) return <p className="muted">Loading…</p>

  const missing = FIELDS.filter((f) => !f.optional && !form[f.key]?.trim())
  const blanksIn = (keys: string[]) =>
    keys.filter((k) => !fieldByKey.get(k)?.optional && !form[k]?.trim()).length

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Organisation</h1>
          <p>
            Wave9's trading identity and the portal's operating settings — edited here so a
            correction never needs a redeploy. Sections with a blank required field open
            themselves; everything saves together.
          </p>
        </div>
        <div className="toolbar">
          {saved && <span className="pill pill--ok">Saved</span>}
          <button className="btn btn--primary" disabled={!dirty || saving} onClick={async () => {
            setSaving(true)
            setError(null)
            try {
              await savePortalSettings(form)
              setDirty(false)
              setSaved(true)
              setTimeout(() => setSaved(false), 2500)
            } catch (e) { setError((e as Error).message) } finally { setSaving(false) }
          }}>{saving ? 'Saving…' : 'Save changes'}</button>
        </div>
      </div>

      {error && <div className="banner banner--stop"><strong>That didn't save.</strong><span>{error}</span></div>}
      {missing.length > 0 && (
        <div className="banner">
          <strong>Still blank:</strong>
          <span>{missing.map((f) => f.label).join(' · ')} — these appear on customer quote documents, so fill them before the pilot.</span>
        </div>
      )}

      {SECTIONS.map((s) => {
        const blanks = blanksIn(s.keys)
        const isOpen = Boolean(open[s.id])
        return (
          <div className="card" key={s.id} style={{ marginBottom: 12 }}>
            <div className="card__head" style={{ cursor: 'pointer', userSelect: 'none' }}
              role="button" aria-expanded={isOpen}
              onClick={() => setOpen({ ...open, [s.id]: !isOpen })}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span aria-hidden style={{ display: 'inline-block', width: 14, transition: 'transform .15s', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▸</span>
                {s.title}
              </h2>
              <div className="toolbar">
                {blanks > 0
                  ? <span className="pill pill--warn">{blanks} blank</span>
                  : <span className="tiny muted">{s.keys.length} setting{s.keys.length === 1 ? '' : 's'}</span>}
              </div>
            </div>
            {isOpen && (
              <div className="card__body">
                <p className="small muted" style={{ marginTop: 0 }}>{s.blurb}</p>
                <div className="split">
                  {s.keys.map((k) => {
                    const f = fieldByKey.get(k)
                    if (!f) return null
                    return (
                      <div className="field" key={f.key} style={f.multiline ? { gridColumn: '1 / -1' } : undefined}>
                        <label>{f.label}{!f.optional && !form[f.key]?.trim() && <span className="pill pill--warn" style={{ marginLeft: 8 }}>blank</span>}</label>
                        {f.multiline
                          ? <textarea className="input" style={{ width: '100%', minHeight: 72, resize: 'vertical' }} value={form[f.key] ?? ''}
                              onChange={(e) => { setForm({ ...form, [f.key]: e.target.value }); setDirty(true) }} />
                          : <input className="input" style={{ width: '100%' }} value={form[f.key] ?? ''}
                              onChange={(e) => { setForm({ ...form, [f.key]: e.target.value }); setDirty(true) }} />}
                        {f.hint && <div className="hint">{f.hint}</div>}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )
      })}
    </>
  )
}
